import { useEffect } from 'react'
import { Button, useMantineColorScheme } from '@mantine/core'

type ThemeMode = 'light' | 'dark' | 'auto'

/* Mantine owns persistence (localStorage key "theme" via the provider's
   colorSchemeManager); this applies the matching Tailwind `.dark` class. */
function applyThemeMode(mode: ThemeMode) {
  const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches
  const resolved = mode === 'auto' ? (prefersDark ? 'dark' : 'light') : mode

  document.documentElement.classList.remove('light', 'dark')
  document.documentElement.classList.add(resolved)

  if (mode === 'auto') {
    document.documentElement.removeAttribute('data-theme')
  } else {
    document.documentElement.setAttribute('data-theme', mode)
  }

  document.documentElement.style.colorScheme = resolved
}

export default function ThemeToggle() {
  const { colorScheme, setColorScheme } = useMantineColorScheme()
  const mode: ThemeMode =
    colorScheme === 'light' || colorScheme === 'dark' ? colorScheme : 'auto'

  useEffect(() => {
    applyThemeMode(mode)
  }, [mode])

  useEffect(() => {
    if (mode !== 'auto') {
      return
    }

    const media = window.matchMedia('(prefers-color-scheme: dark)')
    const onChange = () => applyThemeMode('auto')

    media.addEventListener('change', onChange)
    return () => {
      media.removeEventListener('change', onChange)
    }
  }, [mode])

  function toggleMode() {
    const nextMode: ThemeMode =
      mode === 'light' ? 'dark' : mode === 'dark' ? 'auto' : 'light'
    setColorScheme(nextMode)
    applyThemeMode(nextMode)
  }

  const label =
    mode === 'auto'
      ? 'Theme mode: auto (system). Click to switch to light mode.'
      : `Theme mode: ${mode}. Click to switch mode.`

  return (
    <Button
      variant="default"
      size="sm"
      onClick={toggleMode}
      aria-label={label}
      title={label}
    >
      {mode === 'auto' ? 'Auto' : mode === 'dark' ? 'Dark' : 'Light'}
    </Button>
  )
}
